import Icon from "@booking/components/atoms/media/Icon";
import { SizeEnum } from "@booking/enums";
import { gaClickAttrs } from "@booking/utils/tracking";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import React from "react";

import { type CommonProps } from "@/types";
import { createId, datasetFlag } from "@/utils/helpers";

import styles from "./Details.module.css";

const ref = "details";

interface DetailsProps extends Partial<CommonProps> {
  children: React.ReactNode;
  invert?: boolean;
  noControl?: boolean;
  noSummaryOnOpen?: boolean;
  open?: boolean;
  summary: React.ReactNode;
  summaryClassName?: string;
  summaryWhenOpen?: React.ReactNode;
  onToggle?: Function;
}

function useDetails(props: DetailsProps) {
  const [open, setOpen] = React.useState(props.open);
  const { color, eventGroup, onToggle } = props;

  function toggleDetails(e: React.SyntheticEvent<HTMLDetailsElement, Event>) {
    const details = e.target as HTMLDetailsElement;

    if (e.target !== e.currentTarget) return;

    setOpen(details.open);
    if (onToggle) {
      onToggle(details);
    }
  }

  return {
    className: clsx(styles[ref], color && styles[color], props.className),
    eventGroup,
    id: props.id || createId(ref),
    invert: !!props.invert,
    noControl: !!props.noControl,
    noSummaryOnOpen: !!props.noSummaryOnOpen,
    open,
    summaryClasses: clsx(props.summaryClassName),
    summaryWhenOpen: props.summaryWhenOpen || props.summary,
    toggleDetails,
  };
}

function Details(props: DetailsProps) {
  const {
    className,
    eventGroup,
    id,
    invert,
    noControl,
    noSummaryOnOpen,
    open,
    summaryClasses,
    summaryWhenOpen,
    toggleDetails,
  } = useDetails(props);

  return (
    <details
      className={className}
      id={id}
      open={open}
      data-controls={datasetFlag(!noControl)}
      data-invert={datasetFlag(invert)}
      data-hide-on-open={datasetFlag(noSummaryOnOpen)}
      onToggle={toggleDetails}
    >
      <summary
        className={summaryClasses}
        {...gaClickAttrs({ eventGroup, click: "show_more_click" })}
      >
        {open ? summaryWhenOpen : props.summary}
        {!noControl && <Icon name={faChevronUp} size={SizeEnum.STANDARD} />}
      </summary>

      {props.children}
    </details>
  );
}

export default Details;
export { Details, type DetailsProps };
